import { useEffect, useRef } from 'react'
import { FormInstance } from 'antd'
import { useStore } from 'effector-react'
import { Effect, Event, Store } from 'effector'
import { useEffectNotifications } from './useEffectNotifications'
import { commitReason } from '../../../../shared/reasonAutocomplete'

type SubmitAction = 'close' | 'createNew'

type Config<TStoreData, TFormValues, TMountPayload> = {
  form: FormInstance<TFormValues>
  mode: 'create' | 'edit'
  transactionId?: string
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void

  store: Store<TStoreData>
  initialState: TStoreData
  setForm: Event<TStoreData>
  mapStoreToForm: (data: TStoreData) => Partial<TFormValues>

  events: {
    addMount: Event<TMountPayload>
    editMount: Event<TMountPayload>
    deleteMount: Event<any>
    getByIdMount: Event<{ id: string }>
  }
  effects: {
    add: Effect<any, any, any>
    edit: Effect<any, any, any>
    delete: Effect<any, any, any>
    getById: Effect<any, any, any>
  }

  buildPayload: (
    values: TFormValues,
    ctx: { mode: 'create' | 'edit'; transactionId: string },
  ) => TMountPayload
  buildDeletePayload?: (data: TStoreData) => any

  historyFields?: Record<string, string>

  messages?: {
    create?: string
    update?: string
    delete?: string
    createError?: string
    updateError?: string
    deleteError?: string
  }
}

const DEFAULT_MESSAGES = {
  create: 'Payment successfully created',
  update: 'Payment successfully updated',
  delete: 'Payment successfully deleted',
  createError: 'Failed to create payment',
  updateError: 'Failed to update payment',
  deleteError: 'Failed to delete payment',
}

export const useTransactionForm = <
  TStoreData extends { id?: string | number },
  TFormValues,
  TMountPayload,
>(
  cfg: Config<TStoreData, TFormValues, TMountPayload>,
) => {
  const {
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store,
    initialState,
    setForm,
    mapStoreToForm,
    events,
    effects,
    buildPayload,
    buildDeletePayload,
    historyFields,
    messages,
  } = cfg

  const storeData = useStore(store)
  const isLoadingForm = useStore(effects.getById.pending)
  const isAdding = useStore(effects.add.pending)
  const isEditing = useStore(effects.edit.pending)
  const isDeleting = useStore(effects.delete.pending)

  const pendingActionRef = useRef<SubmitAction>('close')
  const lastInitializedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (transactionId && isOpen) {
      events.getByIdMount({ id: transactionId })
    }
  }, [transactionId, isOpen])

  useEffect(() => {
    if (
      mode === 'edit' &&
      !isLoadingForm &&
      storeData.id &&
      String(storeData.id) === String(transactionId) &&
      lastInitializedIdRef.current !== String(storeData.id)
    ) {
      lastInitializedIdRef.current = String(storeData.id)
      form.setFieldsValue(mapStoreToForm(storeData) as any)
    }
  }, [mode, transactionId, isLoadingForm, storeData, form])

  const handleResetForm = () => {
    form.resetFields()
    setForm(initialState)
    lastInitializedIdRef.current = null
  }

  const handleClose = () => {
    handleResetForm()
    onClose()
  }

  const handleCancel = () => {
    handleResetForm()
    ;(onCancel ?? onClose)()
  }

  const m = { ...DEFAULT_MESSAGES, ...messages }

  useEffectNotifications([
    {
      effect: effects.add,
      successMessage: m.create,
      errorMessage: m.createError,
      onSuccess: () => {
        if (historyFields) {
          const values = form.getFieldsValue() as Record<string, unknown>
          Object.entries(historyFields).forEach(([fieldName, scope]) => {
            const v = values[fieldName]
            if (typeof v === 'string') commitReason(scope, v)
          })
        }
        if (pendingActionRef.current === 'createNew') {
          handleResetForm()
          pendingActionRef.current = 'close'
        } else {
          handleClose()
        }
      },
    },
    {
      effect: effects.edit,
      successMessage: m.update,
      errorMessage: m.updateError,
      onSuccess: handleClose,
    },
    {
      effect: effects.delete,
      successMessage: m.delete,
      errorMessage: m.deleteError,
      onSuccess: handleClose,
    },
  ])

  const submit = async (action: SubmitAction) => {
    pendingActionRef.current = action
    try {
      const values = await form.validateFields()
      const payload = buildPayload(values, {
        mode,
        transactionId: transactionId ?? '',
      })
      if (mode === 'edit' && transactionId) {
        events.editMount(payload)
      } else {
        events.addMount(payload)
      }
    } catch {
      pendingActionRef.current = 'close'
    }
  }

  const handleSubmit = () => submit('close')
  const handleSubmitAndCreateNew = () => submit('createNew')

  const handleDelete = () => {
    const payload = buildDeletePayload
      ? buildDeletePayload(storeData)
      : { id: String(storeData.id ?? '') }
    events.deleteMount(payload)
  }

  return {
    storeData,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleClose,
    handleCancel,
    handleDelete,
    isSubmitting: isAdding || isEditing,
    isDeleting,
    isLoadingForm,
  }
}
